import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  hasError?: boolean
  leadingIcon?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type, hasError, leadingIcon, ...props },
  ref,
) {
  return (
    <div className="relative">
      {leadingIcon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {leadingIcon}
        </span>
      )}
      <input
        ref={ref}
        type={type}
        className={cn(
          'flex h-11 w-full rounded-xl border-[1.5px] bg-background px-4 py-2.5 text-[15px]',
          'transition-colors placeholder:text-muted-foreground',
          'focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          leadingIcon && 'pl-10',
          hasError
            ? 'border-destructive focus-visible:border-destructive'
            : 'border-input focus-visible:border-secondary',
          className,
        )}
        {...props}
      />
    </div>
  )
})

/**
 * Password input con toggle "occhio" come nel mobile.
 */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputProps, 'type'>>(
  function PasswordInput({ className, ...props }, ref) {
    const [visible, setVisible] = useState(false)
    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={cn('pr-10', className)}
          {...props}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={visible ? 'Nascondi password' : 'Mostra password'}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    )
  },
)
